import { 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Avatar, 
  Grid, 
  Box, 
  Chip,
  Pagination,
  alpha,
  Skeleton,
  TextField,
  InputAdornment
} from "@mui/material";
import { Container } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import CustomAppBar from "../../components/shared/CustomAppBar";
import { Broker } from "../../types/Property";
import { fetchAllRealtors } from "../../services/realtorService";

export const BrokersPage: React.FC = () => {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [filteredBrokers, setFilteredBrokers] = useState<Broker[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  
  const brokersPerPage = 12;
  const totalPages = Math.ceil(filteredBrokers.length / brokersPerPage);
  const startIndex = (page - 1) * brokersPerPage;
  const currentBrokers = filteredBrokers.slice(startIndex, startIndex + brokersPerPage);

  useEffect(() => {
    const fetchBrokers = async () => {
      try {
        setLoading(true);
        const response = await fetchAllRealtors();
        setBrokers(response);
        setFilteredBrokers(response);
      } catch (error) {
        console.error('Error fetching brokers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrokers();
  }, []);

  // Filter brokers based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBrokers(brokers);
    } else {
      const filtered = brokers.filter(broker =>
        broker.contacts.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broker.contacts.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broker.contacts.phone.includes(searchTerm) ||
        (broker.teamName && broker.teamName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredBrokers(filtered);
    }
    setPage(1); // Reset to first page when searching
  }, [searchTerm, brokers]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleViewProfile = (realtorId: number) => {
    navigate(`/properties/realtor/${realtorId}`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const LoadingSkeleton = () => (
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      {Array.from(new Array(12)).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Card 
            elevation={0}
            sx={{ 
              height: 320,
              background: 'linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%)',
              border: '1px solid #e0e0e0'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="circular" width={80} height={80} sx={{ mb: 1 }} />
                <Skeleton variant="text" width={120} height={24} />
              </Box>
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" width={60} height={24} />
            </CardContent>
            <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
              <Skeleton variant="rectangular" width="100%" height={36} />
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      <CustomAppBar />
      <Box
        sx={{
          minHeight: '100vh',
          background: "#fff",
          pt: '10vh'
        }}
      >
        <Container sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
          {/* Header */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mb: { xs: 3, sm: 4, md: 5 },
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: { xs: 2, sm: 3, md: 4 },
                p: { xs: 2, sm: 2.5, md: 3 },
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '350px', sm: 'none' }
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  borderRadius: '50%',
                  p: { xs: 1, sm: 1.2, md: 1.5 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)'
                }}
              >
                <BusinessIcon 
                  sx={{ 
                    fontSize: { xs: 18, sm: 20, md: 24 }, 
                    color: 'white'
                  }} 
                />
              </Box>
              <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography 
                  variant="h3" 
                  component="h1"
                  sx={{ 
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: { xs: 0.3, sm: 0.5 },
                    fontSize: { xs: 18, sm: 24, md: 28 }
                  }}
                >
                  Red de Brokers
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: '#388e3c',
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                  }}
                >
                  {filteredBrokers.length} brokers profesionales
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Search Input */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mb: { xs: 3, sm: 4, md: 5 },
            px: { xs: 1, sm: 0 }
          }}>
            <TextField
              placeholder="Buscar brokers por nombre, email, teléfono o equipo..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              fullWidth
              sx={{
                maxWidth: { xs: '100%', sm: 500, md: 600 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: { xs: 6, sm: 8 },
                  background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.95) 100%)',
                  backdropFilter: 'blur(15px)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  boxShadow: '0 8px 32px rgba(76, 175, 80, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 12px 40px rgba(76, 175, 80, 0.25)',
                    transform: 'translateY(-2px)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 16px 48px rgba(76, 175, 80, 0.3)',
                    transform: 'translateY(-3px)',
                    background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.98) 100%)'
                  },
                  '& fieldset': {
                    border: 'none'
                  },
                  '& input': {
                    padding: { xs: '12px 14px', sm: '14px 16px', md: '16px 18px' },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    color: '#2e7d32',
                    fontWeight: 500,
                    '&::placeholder': {
                      color: '#81c784',
                      opacity: 0.8
                    }
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                        borderRadius: '50%',
                        p: { xs: 0.8, sm: 1 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1,
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                      }}
                    >
                      <SearchIcon 
                        sx={{ 
                          color: 'white', 
                          fontSize: { xs: 18, sm: 20 }
                        }} 
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Loading State */}
          {loading && <LoadingSkeleton />}

          {/* Brokers Grid */}
          {!loading && filteredBrokers.length > 0 && (
            <>
              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                {currentBrokers.map((broker) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={broker.userProfileId}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.95) 100%)',
                        backdropFilter: 'blur(15px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: { xs: 2, sm: 2.5, md: 3 },
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: '0 20px 40px rgba(76, 175, 80, 0.3)',
                          '&::before': {
                            opacity: 1
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: 'linear-gradient(90deg, #4caf50 0%, #81c784 50%, #4caf50 100%)',
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease'
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, pb: 2, pt: { xs: 2, sm: 2.5, md: 3 } }}>
                        {/* Profile Image and Name */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: { xs: 2, sm: 2.5, md: 3 } }}>
                          <Box sx={{ position: 'relative', mb: { xs: 1, sm: 1.2, md: 1.5 } }}>
                            <Avatar
                              src={broker.profileImage}
                              alt={broker.contacts.fullName}
                              sx={{ 
                                width: { xs: 70, sm: 80, md: 90 }, 
                                height: { xs: 70, sm: 80, md: 90 },
                                border: '4px solid transparent',
                                background: 'linear-gradient(135deg, #4caf50, #81c784) padding-box, linear-gradient(135deg, #4caf50, #81c784) border-box',
                                boxShadow: '0 8px 24px rgba(76, 175, 80, 0.3)'
                              }}
                            >
                              <PersonIcon sx={{ fontSize: { xs: 35, sm: 40, md: 45 }, color: '#4caf50' }} />
                            </Avatar>
                            {broker.isLeader && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: { xs: -2, sm: -3, md: -4 },
                                  right: { xs: -2, sm: -3, md: -4 },
                                  background: 'linear-gradient(135deg, #ffd700 0%, #ffab00 100%)',
                                  borderRadius: '50%',
                                  p: { xs: 0.3, sm: 0.4, md: 0.5 },
                                  boxShadow: '0 2px 8px rgba(255, 193, 7, 0.4)'
                                }}
                              >
                                <StarIcon sx={{ fontSize: { xs: 12, sm: 14, md: 16 }, color: 'white' }} />
                              </Box>
                            )}
                          </Box>
                          
                          <Typography 
                            variant="h6" 
                            component="h2"
                            sx={{ 
                              fontWeight: 700,
                              textAlign: 'center',
                              color: '#2e7d32',
                              lineHeight: 1.2,
                              mb: { xs: 0.5, sm: 0.8, md: 1 },
                              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                            }}
                          >
                            {broker.contacts.fullName}
                          </Typography>
                          
                          {broker.isLeader && (
                            <Chip 
                              label="Líder de Equipo" 
                              size="small"
                              icon={<StarIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                              sx={{ 
                                background: 'linear-gradient(135deg, #ffd700 0%, #ffab00 100%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                height: { xs: 20, sm: 22, md: 24 }
                              }}
                            />
                          )}
                        </Box>

                        {/* Contact Information */}
                        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                          {/* Email */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            mb: { xs: 1, sm: 1.2, md: 1.5 },
                            p: { xs: 1, sm: 1.2, md: 1.5 },
                            borderRadius: { xs: 1.5, sm: 2 },
                            background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)',
                            border: '1px solid #c8e6c9'
                          }}>
                            <EmailIcon 
                              sx={{ 
                                fontSize: { xs: 16, sm: 17, md: 18 }, 
                                mr: { xs: 1, sm: 1.2, md: 1.5 }, 
                                mt: 0.2,
                                color: '#4caf50'
                              }} 
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                                wordBreak: 'break-word',
                                color: '#2e7d32',
                                fontWeight: 500,
                                lineHeight: 1.3
                              }}
                            >
                              {broker.contacts.email}
                            </Typography>
                          </Box>

                          {/* Phone */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            p: { xs: 1, sm: 1.2, md: 1.5 },
                            borderRadius: { xs: 1.5, sm: 2 },
                            background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)',
                            border: '1px solid #c8e6c9'
                          }}>
                            <PhoneIcon 
                              sx={{ 
                                fontSize: { xs: 16, sm: 17, md: 18 }, 
                                mr: { xs: 1, sm: 1.2, md: 1.5 }, 
                                color: '#4caf50'
                              }} 
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                                color: '#2e7d32',
                                fontWeight: 500
                              }}
                            >
                              {broker.contacts.phone}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Team */}
                        {broker.teamName && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Chip 
                              label={broker.teamName}
                              icon={<GroupIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                borderColor: '#4caf50',
                                color: '#2e7d32',
                                background: 'rgba(76, 175, 80, 0.05)',
                                fontWeight: 600,
                                height: { xs: 20, sm: 22, md: 24 },
                                '&:hover': {
                                  background: 'rgba(76, 175, 80, 0.1)'
                                }
                              }}
                            />
                          </Box>
                        )}
                      </CardContent>

                      <CardActions sx={{ pt: 0, pb: { xs: 2, sm: 2.5, md: 3 }, px: { xs: 2, sm: 2.5, md: 3 } }}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => handleViewProfile(broker.id)}
                          sx={{
                            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                            borderRadius: { xs: 2, sm: 2.2, md: 2.5 },
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                            py: { xs: 1, sm: 1.1, md: 1.2 },
                            boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          Ver Perfil Completo
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  mt: { xs: 4, sm: 5 },
                  mb: 2,
                  px: { xs: 1, sm: 0 }
                }}>
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.95) 100%)',
                      backdropFilter: 'blur(15px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: { xs: 2, sm: 2.5, md: 3 },
                      p: { xs: 1.5, sm: 2 },
                      boxShadow: '0 8px 32px rgba(76, 175, 80, 0.2)',
                      width: { xs: '100%', sm: 'auto' },
                      overflow: 'auto'
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontWeight: 600,
                          borderRadius: { xs: 1.5, sm: 2 },
                          margin: { xs: '0 1px', sm: '0 2px' },
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          minWidth: { xs: 28, sm: 32 },
                          height: { xs: 28, sm: 32 },
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                          },
                          '&:hover': {
                            background: alpha('#4caf50', 0.1)
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Empty State */}
          {!loading && filteredBrokers.length === 0 && (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 10,
                background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.9) 100%)',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.2)'
              }}
            >
              {searchTerm ? (
                <>
                  <SearchIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: '#81c784',
                      mb: 3,
                      opacity: 0.7
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{
                      color: '#388e3c',
                      fontWeight: 700,
                      textAlign: 'center',
                      mb: 1
                    }}
                  >
                    No se encontraron brokers
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{
                      color: '#66bb6a',
                      textAlign: 'center',
                      maxWidth: 400,
                      mb: 2
                    }}
                  >
                    No hay brokers que coincidan con "{searchTerm}"
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      borderColor: '#4caf50',
                      color: '#4caf50',
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'rgba(76, 175, 80, 0.1)',
                        borderColor: '#388e3c'
                      }
                    }}
                  >
                    Limpiar búsqueda
                  </Button>
                </>
              ) : (
                <>
                  <BusinessIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: '#81c784',
                      mb: 3,
                      opacity: 0.7
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{
                      color: '#388e3c',
                      fontWeight: 700,
                      textAlign: 'center',
                      mb: 1
                    }}
                  >
                    No hay brokers disponibles
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{
                      color: '#66bb6a',
                      textAlign: 'center',
                      maxWidth: 400
                    }}
                  >
                    Los brokers aparecerán aquí cuando estén disponibles en nuestra red profesional
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};